import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MdSearch } from 'react-icons/md';

import type { RootState } from '../../redux/store';

import {
  fetchTemplatesLibraryRequest,
} from '../../redux/slices/templatesLibrarySlice';

import type { MediaType } from '../pexels/types';

interface TemplatesLibraryProps {
  onSelectMedia?: (url: string, type: MediaType) => void;
}

const TemplatesLibrary: React.FC<TemplatesLibraryProps> = ({
  onSelectMedia,
}) => {
  const dispatch = useDispatch();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('all');

  const restaurantId = useSelector(
    (state: RootState) => state.clover.restaurantId
  );

  const {
    templates,
    loading,
    error,
  } = useSelector(
    (state: RootState) => state.templatesLibrary
  );

  // Fetch templates
  useEffect(() => {
    if (!restaurantId) return;

    dispatch(
      fetchTemplatesLibraryRequest({
        restaurantId,
        serviceType: 'promoflow',
      })
    );
  }, [dispatch, restaurantId]);

  // Get unique industries
  const industries = useMemo(() => {
    return Array.from(
      new Set(
        templates
          .map((template) => template.industry)
          .filter(Boolean)
      )
    );
  }, [templates]);

  // Filter templates
  const filteredTemplates = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return templates.filter((template) => {
      const matchesIndustry =
        selectedIndustry === 'all' ||
        template.industry?.toLowerCase() ===
          selectedIndustry.toLowerCase();

      const matchesSearch =
        !query ||
        template.title?.toLowerCase().includes(query) ||
        template.industry?.toLowerCase().includes(query) ||
        template.category?.toLowerCase().includes(query) ||
        template.subCategory?.toLowerCase().includes(query) ||
        template.templateType?.toLowerCase().includes(query) ||
        template.tags?.some((tag) =>
          tag.toLowerCase().includes(query)
        );

      return matchesIndustry && matchesSearch;
    });
  }, [templates, searchQuery, selectedIndustry]);

  // Handle template selection
  // Same callback pattern as PexelsMediaSearch
  const handleTemplateClick = (url: string) => {
    if (onSelectMedia) {
      onSelectMedia(url, 'photos');
    }
  };

  // Template skeleton
  const SkeletonCard = () => (
    <div className="relative w-full aspect-[3/4] rounded-lg overflow-hidden bg-gray-800 animate-pulse">
      <div className="absolute inset-0 bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800" />
    </div>
  );

  // Initial loading
  if (loading && templates.length === 0) {
    return (
      <div className="w-full h-full flex flex-col min-h-0">

        {/* Search / Filter Skeleton */}
        <div className="flex flex-col sm:flex-row gap-3 px-4 pt-4 pb-2">

          {/* Search Skeleton */}
          <div className="flex-1 h-12 rounded-lg bg-gray-800 animate-pulse" />

          {/* Dropdown Skeleton */}
          <div className="sm:w-52 h-12 rounded-lg bg-gray-800 animate-pulse" />

        </div>

        {/* Template Skeleton Grid */}
        <div className="w-full flex-1 min-h-0 overflow-y-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 p-4">
            {[...Array(15)].map((_, index) => (
              <SkeletonCard key={index} />
            ))}
          </div>
        </div>

      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center p-6">
        <p className="text-sm text-red-400">
          {error}
        </p>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col min-h-0">

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 px-4 pt-4 pb-2">

        {/* Search */}
        <div className="relative flex-1">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search templates"
            className="w-full px-4 py-3 pr-12 bg-[#2a2a2a] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-primary"
          />

          <button
            type="button"
            onClick={() => {
              // Search is handled automatically by searchQuery
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-brand-primary transition-colors"
          >
            <MdSearch size={24} />
          </button>
        </div>

        {/* Industry Dropdown */}
        <div className="sm:w-52">
          <select
            value={selectedIndustry}
            onChange={(e) => setSelectedIndustry(e.target.value)}
            className="w-full h-full min-h-[48px] px-4 py-3 bg-[#2a2a2a] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-brand-primary cursor-pointer"
          >
            <option value="all">
              All Industries
            </option>

            {industries.map((industry) => (
              <option
                key={industry}
                value={industry}
              >
                {industry}
              </option>
            ))}
          </select>
        </div>

      </div>

      {/* Templates */}
      <div className="w-full flex-1 min-h-0 overflow-y-auto max-h-[58vh]">

        {filteredTemplates.length === 0 ? (
          <div className="w-full h-full flex items-center justify-center p-6">
            <div className="text-center">

              <MdSearch
                size={48}
                className="mx-auto mb-3 text-gray-600"
              />

              <p className="text-sm text-gray-400">
                No templates found
              </p>

              {(searchQuery || selectedIndustry !== 'all') && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedIndustry('all');
                  }}
                  className="mt-2 text-sm text-brand-primary hover:underline"
                >
                  Clear filters
                </button>
              )}

            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 p-4">

            {filteredTemplates.map((template) => (
              <div
                key={template.id}
                onClick={() =>
                  handleTemplateClick(template.image.url)
                }
                className="group cursor-pointer w-full"
              >
                <img
                  src={
                    template.image.thumbnailUrl ||
                    template.image.url
                  }
                  alt={template.title}
                  loading="lazy"
                  className="w-full aspect-[3/4] rounded-lg object-cover transition-transform duration-200 group-hover:scale-[1.02]"
                />
              </div>
            ))}

          </div>
        )}

      </div>
    </div>
  );
};

export default TemplatesLibrary;